import { useEffect, useRef } from 'react';

/**
 * AURORA DS — „Żywa sieć" na ekranie logowania.
 * Wierny port animacji [data-authneuro] z handoffu Claude Design (app.js):
 * rdzeń + ~36 oddziałów na kuli Fibonacciego, każdy z 12–30 węzłami,
 * impulsy i strzały wzdłuż synaps, zapłony nitek, rotacja 3D.
 * Czysto dekoracyjny (aria-hidden, pointer-events none w .auth-hero__orbit).
 */

type NeuroNode = {
  x: number;
  y: number;
  z: number;
  c: string;
  r: number;
  ph: number;
  core?: boolean;
  hub?: boolean;
  da?: number;
  dv?: number;
  amp?: number;
  px?: number;
  py?: number;
  sc?: number;
  depth?: number;
};
type NeuroEdge = [NeuroNode, NeuroNode, string, number];

const HUES = [
  '167,139,250',
  '96,165,250',
  '163,230,53',
  '45,212,191',
  '34,211,238',
  '251,191,36',
  '248,113,113',
  '244,114,182',
  '129,140,248',
  '52,211,153',
];

export function AuroraNeuroCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    const reduceM = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dpr = Math.min(devicePixelRatio || 1, 1.5);
    let W = 0,
      H = 0,
      CX = 0,
      CY = 0,
      S = 0;
    let nodes: NeuroNode[] = [];
    let edges: NeuroEdge[] = [];
    let pulses: { e: NeuroEdge; p: number; v: number }[] = [];
    let shots: { e: NeuroEdge; p: number; v: number }[] = [];
    let flashes: { x: number; y: number; r: number; a: number; c: string }[] = [];
    let burns: { e: NeuroEdge; ph: number; t0: number }[] = [];
    let raf: number | null = null;
    let rotY = 0;
    let rotX = 0.22;

    function build() {
      const parent = cv!.parentElement;
      if (!parent) return;
      const r = parent.getBoundingClientRect();
      if (r.width < 10) return;
      W = cv!.width = r.width * dpr;
      H = cv!.height = r.height * dpr;
      CX = W * 0.5;
      CY = H * 0.5;
      S = Math.min(W, H) * 0.55;
      nodes = [];
      edges = [];
      pulses = [];
      shots = [];
      flashes = [];
      burns = [];
      // rdzeń
      const core: NeuroNode = { x: 0, y: 0, z: 0, core: true, c: '167,139,250', r: 9, ph: 0 };
      nodes.push(core);
      // ~36 oddziałów (hubów) rozrzuconych na sferze, każdy z 12–30 pracownikami
      const NB = 36;
      for (let b = 0; b < NB; b++) {
        const th = Math.acos(1 - (2 * (b + 0.5)) / NB);
        const phi = b * 2.399963; // fibonacci sphere
        const R0 = S * (0.5 + Math.random() * 0.5);
        const hx = Math.sin(th) * Math.cos(phi) * R0;
        const hy = Math.cos(th) * R0 * 0.7;
        const hz = Math.sin(th) * Math.sin(phi) * R0;
        const c = HUES[b % HUES.length];
        const hub: NeuroNode = { x: hx, y: hy, z: hz, c, r: 3.4, hub: true, ph: Math.random() * 7 };
        nodes.push(hub);
        edges.push([core, hub, c, 0.5]);
        const n = 12 + Math.floor(Math.random() * 19);
        let prev = hub;
        for (let i = 0; i < n; i++) {
          const dd = S * 0.05 + Math.random() * S * 0.13;
          const a = Math.random() * Math.PI * 2;
          const e = Math.random() * Math.PI;
          const nd: NeuroNode = {
            x: hx + Math.sin(e) * Math.cos(a) * dd,
            y: hy + Math.cos(e) * dd,
            z: hz + Math.sin(e) * Math.sin(a) * dd,
            c,
            r: 0.9 + Math.random() * 1.5,
            ph: Math.random() * 7,
            da: Math.random() * 7,
            dv: (0.00012 + Math.random() * 0.00022) * (Math.random() < 0.5 ? -1 : 1),
            amp: S * 0.008 * (0.5 + Math.random()),
          };
          nodes.push(nd);
          edges.push([Math.random() < 0.75 ? hub : prev, nd, c, 0.18]);
          prev = nd;
        }
      }
      // synapsy między oddziałami
      const hubs = nodes.filter((n) => n.hub);
      for (let i = 0; i < 26; i++) {
        const a = hubs[Math.floor(Math.random() * hubs.length)];
        const b = hubs[Math.floor(Math.random() * hubs.length)];
        if (a !== b) edges.push([a, b, '148,163,184', 0.22]);
      }
    }

    function tick(t: number) {
      ctx!.clearRect(0, 0, W, H);
      if (!reduceM) {
        rotY += 0.00045;
        rotX = 0.22 + Math.sin(t / 16000) * 0.07;
      }
      const sy = Math.sin(rotY),
        cy2 = Math.cos(rotY),
        sx = Math.sin(rotX),
        cx2 = Math.cos(rotX);
      const persp = S * 2.6;
      // projekcja
      nodes.forEach((n) => {
        let x = n.x,
          y = n.y;
        const z = n.z;
        if (!n.core && n.dv !== undefined) {
          n.da! += n.dv * 16;
          x += Math.cos(n.da!) * n.amp!;
          y += Math.sin(n.da! * 1.3) * n.amp!;
        }
        // rotacja Y potem X
        const x1 = x * cy2 - z * sy,
          z1 = x * sy + z * cy2;
        const y1 = y * cx2 - z1 * sx,
          z2 = y * sx + z1 * cx2;
        const sc = persp / (persp + z2);
        n.px = CX + x1 * sc;
        n.py = CY + y1 * sc;
        n.sc = sc;
        n.depth = z2;
      });
      // krawędzie (za węzłami)
      ctx!.lineWidth = 0.7 * dpr;
      edges.forEach((e) => {
        const d = ((e[0].sc! + e[1].sc!) / 2 - 0.7) * 1.6; // bliżej = wyraźniej
        ctx!.strokeStyle = `rgba(${e[2]},${Math.max(0.03, e[3] * Math.max(0.15, d))})`;
        ctx!.beginPath();
        ctx!.moveTo(e[0].px!, e[0].py!);
        ctx!.lineTo(e[1].px!, e[1].py!);
        ctx!.stroke();
      });
      // impulsy
      if (!reduceM) {
        if (pulses.length < 12 && Math.random() < 0.05)
          pulses.push({
            e: edges[Math.floor(Math.random() * edges.length)],
            p: 0,
            v: 0.0025 + Math.random() * 0.004,
          });
        pulses = pulses.filter((pl) => pl.p <= 1);
        pulses.forEach((pl) => {
          pl.p += pl.v;
          const x = pl.e[0].px! + (pl.e[1].px! - pl.e[0].px!) * pl.p;
          const y = pl.e[0].py! + (pl.e[1].py! - pl.e[0].py!) * pl.p;
          const g = ctx!.createRadialGradient(x, y, 0, x, y, 7 * dpr);
          g.addColorStop(0, `rgba(${pl.e[2]},0.95)`);
          g.addColorStop(1, `rgba(${pl.e[2]},0)`);
          ctx!.fillStyle = g;
          ctx!.beginPath();
          ctx!.arc(x, y, 7 * dpr, 0, 7);
          ctx!.fill();
        });
        // strzały — szybkie flashe wzdłuż synaps (jak w Żywej sieci)
        if (shots.length < 3 && Math.random() < 0.016)
          shots.push({
            e: edges[Math.floor(Math.random() * edges.length)],
            p: 0,
            v: 0.022 + Math.random() * 0.02,
          });
        shots = shots.filter((s) => s.p <= 1.001);
        shots.forEach((s) => {
          s.p = Math.min(1, s.p + s.v);
          const e = s.e;
          const tail = Math.max(0, s.p - 0.22);
          const x1 = e[0].px! + (e[1].px! - e[0].px!) * tail;
          const y1 = e[0].py! + (e[1].py! - e[0].py!) * tail;
          const x2 = e[0].px! + (e[1].px! - e[0].px!) * s.p;
          const y2 = e[0].py! + (e[1].py! - e[0].py!) * s.p;
          const lg = ctx!.createLinearGradient(x1, y1, x2, y2);
          lg.addColorStop(0, `rgba(${e[2]},0)`);
          lg.addColorStop(0.7, `rgba(${e[2]},.9)`);
          lg.addColorStop(1, 'rgba(255,255,255,.95)');
          ctx!.strokeStyle = lg;
          ctx!.lineWidth = 1.8 * dpr;
          ctx!.lineCap = 'round';
          ctx!.beginPath();
          ctx!.moveTo(x1, y1);
          ctx!.lineTo(x2, y2);
          ctx!.stroke();
          if (s.p >= 1) {
            flashes.push({ x: e[1].px!, y: e[1].py!, r: 2 * dpr, a: 0.9, c: e[2] });
            burns.push({ e: s.e, ph: 0, t0: t });
            s.p = 2;
          }
        });
        // zapłon nitki: cała synapsa świeci, potem „pociąg" gaśnie od źródła
        burns = burns.filter((b) => b.ph < 1);
        burns.forEach((b) => {
          const e = b.e;
          const age = t - b.t0;
          const HOLD = 420,
            DECAY = 1300;
          const x0 = e[0].px!,
            y0 = e[0].py!,
            x1 = e[1].px!,
            y1 = e[1].py!;
          if (age < HOLD) {
            // pełny zapłon — lekki żywy flicker
            const fl = 0.78 + Math.sin(age / 38) * 0.14;
            ctx!.strokeStyle = `rgba(255,255,255,${fl * 0.5})`;
            ctx!.lineWidth = 2.6 * dpr;
            ctx!.lineCap = 'round';
            ctx!.beginPath();
            ctx!.moveTo(x0, y0);
            ctx!.lineTo(x1, y1);
            ctx!.stroke();
            ctx!.strokeStyle = `rgba(${e[2]},${fl})`;
            ctx!.lineWidth = 1.4 * dpr;
            ctx!.beginPath();
            ctx!.moveTo(x0, y0);
            ctx!.lineTo(x1, y1);
            ctx!.stroke();
          } else {
            // odcięte źródło — front gasnięcia jedzie od początku do końca
            const p = (age - HOLD) / DECAY;
            b.ph = p;
            if (p < 1) {
              const fx = x0 + (x1 - x0) * p;
              const fy = y0 + (y1 - y0) * p;
              const rem = 1 - p; // pozostała świecąca część [p..1]
              const lg = ctx!.createLinearGradient(fx, fy, x1, y1);
              lg.addColorStop(0, `rgba(${e[2]},${0.9 * rem})`);
              lg.addColorStop(1, `rgba(${e[2]},${0.25 * rem})`);
              ctx!.strokeStyle = lg;
              ctx!.lineWidth = 1.6 * dpr;
              ctx!.lineCap = 'round';
              ctx!.beginPath();
              ctx!.moveTo(fx, fy);
              ctx!.lineTo(x1, y1);
              ctx!.stroke();
              // tylne światło pociągu — jasny punkt na froncie gasnięcia
              const g = ctx!.createRadialGradient(fx, fy, 0, fx, fy, 6 * dpr);
              g.addColorStop(0, `rgba(255,255,255,${0.85 * rem})`);
              g.addColorStop(0.4, `rgba(${e[2]},${0.7 * rem})`);
              g.addColorStop(1, `rgba(${e[2]},0)`);
              ctx!.fillStyle = g;
              ctx!.beginPath();
              ctx!.arc(fx, fy, 6 * dpr, 0, 7);
              ctx!.fill();
            }
          }
        });
        flashes = flashes.filter((f) => f.a > 0.02);
        flashes.forEach((f) => {
          f.r += 1.7 * dpr;
          f.a *= 0.9;
          ctx!.beginPath();
          ctx!.arc(f.x, f.y, f.r, 0, 7);
          ctx!.strokeStyle = `rgba(${f.c},${f.a})`;
          ctx!.lineWidth = 1.6 * dpr;
          ctx!.stroke();
          const g = ctx!.createRadialGradient(f.x, f.y, 0, f.x, f.y, f.r * 0.8);
          g.addColorStop(0, `rgba(255,255,255,${f.a * 0.55})`);
          g.addColorStop(1, 'rgba(255,255,255,0)');
          ctx!.fillStyle = g;
          ctx!.beginPath();
          ctx!.arc(f.x, f.y, f.r * 0.8, 0, 7);
          ctx!.fill();
        });
      }
      // węzły — sortowane po głębi (dalsze najpierw)
      const sorted = nodes.slice().sort((a, b) => b.depth! - a.depth!);
      sorted.forEach((n) => {
        const b = reduceM ? 0.4 : (Math.sin(t / 1600 + n.ph) + 1) / 2;
        const rad = Math.max(0.5, n.r * dpr * n.sc! * (1 + b * 0.3));
        const alpha = Math.max(0.12, Math.min(1, (n.sc! - 0.55) * 1.8));
        if (n.core) {
          const cg = ctx!.createRadialGradient(
            n.px! - 3 * dpr,
            n.py! - 3 * dpr,
            0,
            n.px!,
            n.py!,
            30 * dpr * n.sc!
          );
          cg.addColorStop(0, 'rgba(237,233,254,.95)');
          cg.addColorStop(0.4, `rgba(167,139,250,${0.7 + b * 0.25})`);
          cg.addColorStop(1, 'rgba(109,40,217,0)');
          ctx!.fillStyle = cg;
          ctx!.beginPath();
          ctx!.arc(n.px!, n.py!, 30 * dpr * n.sc!, 0, 7);
          ctx!.fill();
          ctx!.beginPath();
          ctx!.arc(n.px!, n.py!, (38 + b * 6) * dpr * n.sc!, 0, 7);
          ctx!.strokeStyle = `rgba(167,139,250,${0.3 + b * 0.25})`;
          ctx!.lineWidth = 1.1 * dpr;
          ctx!.stroke();
          return;
        }
        if (n.hub) {
          const g = ctx!.createRadialGradient(n.px!, n.py!, 0, n.px!, n.py!, rad * 3.4);
          g.addColorStop(0, `rgba(${n.c},${(0.55 + b * 0.3) * alpha})`);
          g.addColorStop(1, `rgba(${n.c},0)`);
          ctx!.fillStyle = g;
          ctx!.beginPath();
          ctx!.arc(n.px!, n.py!, rad * 3.4, 0, 7);
          ctx!.fill();
        }
        ctx!.fillStyle = `rgba(${n.c},${(n.hub ? 0.95 : 0.55 + b * 0.35) * alpha})`;
        ctx!.beginPath();
        ctx!.arc(n.px!, n.py!, rad, 0, 7);
        ctx!.fill();
      });
      raf = requestAnimationFrame(tick);
    }

    function start() {
      if (raf) cancelAnimationFrame(raf);
      build();
      raf = requestAnimationFrame(tick);
    }

    const onResize = () => start();
    addEventListener('resize', onResize);
    start();

    return () => {
      removeEventListener('resize', onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return <canvas ref={ref} aria-hidden="true" />;
}
